// Kleine bouwstenen die de drie tabbladen van Bronnen & dekking delen (W6 ronde 3).
//
// Bewust hier en niet in components/ui: een handvol opmaak en één balk voor
// één vlak, geen gedrag dat de rest van het dashboard nodig heeft (dezelfde
// afweging als stijl.ts).
//
// De regel die hier het zwaarst weegt (research §3.4, W8-ontwerpregel 1): het
// scherm rekent geen aandeel uit. `procent` maakt alleen een getal op dat de
// server al stuurde. Een balk mag voor het TEKENEN n/schaal gebruiken, maar
// zijn label is dan de telling — nooit een percentage dat de server niet gaf.

import type { ReactNode } from "react";

import { Card } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import { chart, color, radius, sectionLabel, size, weight } from "../../tokens";

/** "2.143" — Nederlandse duizendtallen. */
export const getal = (n: number) => n.toLocaleString("nl-NL");

/** Een aandeel zoals de server het stuurt: 42.0 → "42%", 10.1 → "10,1%". */
export function procent(p: number): string {
  return `${p.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}%`;
}

/** "1 stem", "3 stemmen". */
export function aantal(n: number, een: string, meer: string): string {
  return `${getal(n)} ${n === 1 ? een : meer}`;
}

/**
 * "19 aug. 2026" — of null als de server niets (leesbaars) stuurde. Een kale
 * datum ("2026-08-19") lezen we als lokale dag: `new Date("2026-08-19")` is
 * middernacht UTC en wordt ten westen van Greenwich de dag ervoor.
 */
export function datum(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const kaal = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = kaal
    ? new Date(Number(kaal[1]), Number(kaal[2]) - 1, Number(kaal[3]))
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}

export const noot = {
  fontSize: 12,
  color: color.faint,
  marginTop: 8,
  lineHeight: 1.5,
} as const;

export const cijfer = { fontVariantNumeric: "tabular-nums" } as const;

/** Een blok met een kop in de stijl van de andere beheervlakken. */
export function Blok({
  titel,
  rechts,
  children,
}: {
  titel: string;
  rechts?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <h2 style={{ ...sectionLabel, marginBottom: 0 }}>{titel}</h2>
        {rechts}
      </div>
      {children}
    </section>
  );
}

/** Groot getal in een kaart (owner-ontwerptaal), zelfde maat als de kaarten
 *  op het Overzicht. Geen getal = de reden op de plek van het getal. */
export function Kpi({
  label,
  waarde,
  reden,
  onder,
}: {
  label: string;
  waarde: string | null;
  reden?: string;
  onder?: string;
}) {
  return (
    <Card style={{ padding: "16px 18px" }}>
      <div style={{ ...sectionLabel, marginBottom: 8 }}>{label}</div>
      {waarde !== null ? (
        <div
          style={{
            fontSize: 25,
            fontWeight: weight.bold,
            color: color.ink,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {waarde}
        </div>
      ) : (
        <EmptyReason reason={reden ?? "de server gaf geen getal"} />
      )}
      {onder && (
        <div style={{ fontSize: 12, color: color.faint, marginTop: 6, lineHeight: 1.5 }}>
          {onder}
        </div>
      )}
    </Card>
  );
}

export type BalkRij = {
  sleutel: string;
  label: string;
  n: number;
  /** Alleen als de server een aandeel stuurde. */
  aandeel?: number | null;
  title?: string;
};

/**
 * Liggende balken van gewone divs. `schaal` bepaalt alleen hoe lang een balk
 * getekend wordt; het label is de telling, plus het aandeel als de server er
 * een gaf. Een neutrale kleur: dit zijn tellingen, geen oordeel — de
 * stoplichten staan bij de bronrollen, waar de server ze zet.
 */
export function Balken({ rijen, schaal }: { rijen: readonly BalkRij[]; schaal: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rijen.map((r) => {
        const breedte = schaal > 0 ? Math.min(100, Math.max(0, (r.n / schaal) * 100)) : 0;
        return (
          <div
            key={r.sleutel}
            data-balk={r.sleutel}
            title={r.title}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(96px, 170px) minmax(40px, 1fr) auto",
              alignItems: "center",
              gap: 10,
              fontSize: size.meta,
            }}
          >
            <span
              style={{
                color: color.body,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {r.label}
            </span>
            <span
              aria-hidden
              style={{
                height: 8,
                background: color.lineFaint,
                borderRadius: radius.pill,
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  display: "block",
                  height: "100%",
                  width: `${breedte}%`,
                  background: chart.slate,
                  borderRadius: radius.pill,
                }}
              />
            </span>
            <span
              style={{
                ...cijfer,
                color: color.ink,
                textAlign: "right",
                whiteSpace: "nowrap",
                minWidth: 48,
              }}
            >
              {getal(r.n)}
              {r.aandeel !== undefined && r.aandeel !== null && (
                <span style={{ color: color.faint }}>{` · ${procent(r.aandeel)}`}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
